import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookTemplate, Plus, Download, Upload, Trash2, Play, Calendar, ShieldCheck, Edit2, History } from "lucide-react";

// ── Edit template metadata modal ──────────────────────────────────
function EditTemplateModal({ template, onSave, onClose, isSuperAdmin }) {
  const [form, setForm] = useState({
    name: template?.name || "",
    description: template?.description || "",
    is_global: template?.is_global || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!form.name) { setError("Name is required."); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit Template</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-gray-500">Template Name *</Label>
            <Input className="mt-1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-template-name" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Description</Label>
            <Input className="mt-1" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          {isSuperAdmin && (
            <label className="flex items-center gap-2 cursor-pointer p-3 bg-[#e6f2f5] rounded-lg border border-[#006080]/20">
              <input type="checkbox" checked={form.is_global} onChange={e => setForm(f => ({ ...f, is_global: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm font-medium text-[#006080] flex items-center gap-1">
                <ShieldCheck size={14} /> Make Global
              </span>
              <span className="text-xs text-gray-400">(visible to all companies)</span>
            </label>
          )}
          <div className="text-xs text-gray-400 bg-[#F8F9FA] rounded p-2">
            To update the pipeline or API definitions, apply this template to a file, edit it there, and save as a new template.
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving} data-testid="save-edit-template-btn">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaveTemplateModal({ uploads, apiDefs, onSave, onClose, isSuperAdmin }) {
  const [form, setForm] = useState({ name: "", description: "", upload_id: "", include_pipeline: true, include_api_defs: true, api_definition_ids: [], sequence_ids: [], is_global: false });
  const [sequences, setSequences] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    client.get("/sequences/").then(r => setSequences(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.name) { setError("Template name is required."); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Save Template</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-gray-500">Template Name *</Label>
            <Input className="mt-1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="My Template" data-testid="template-name-input" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Description</Label>
            <Input className="mt-1" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Source File (optional)</Label>
            <Select value={form.upload_id} onValueChange={v => setForm(f => ({ ...f, upload_id: v }))}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select file to copy pipeline from..." /></SelectTrigger>
              <SelectContent>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.include_pipeline} onChange={e => setForm(f => ({ ...f, include_pipeline: e.target.checked }))} />
              <span className="text-sm text-gray-700">Include Pipeline</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.include_api_defs} onChange={e => setForm(f => ({ ...f, include_api_defs: e.target.checked }))} />
              <span className="text-sm text-gray-700">Include API Defs</span>
            </label>
            {isSuperAdmin && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_global} onChange={e => setForm(f => ({ ...f, is_global: e.target.checked }))} />
                <span className="text-sm font-medium text-[#006080] flex items-center gap-1">
                  <ShieldCheck size={13} /> Make Global
                </span>
              </label>
            )}
          </div>
          {form.include_api_defs && (
            <div>
              <Label className="text-xs text-gray-500">API Definitions to include</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {apiDefs.map(d => {
                  const sel = form.api_definition_ids.includes(d._id);
                  return (
                    <button
                      key={d._id}
                      className={`text-xs px-2 py-1 rounded border transition-colors ${sel ? "bg-[#006080] text-white border-[#006080]" : "bg-white border-gray-200 text-gray-600"}`}
                      onClick={() => setForm(f => ({
                        ...f,
                        api_definition_ids: sel ? f.api_definition_ids.filter(x => x !== d._id) : [...f.api_definition_ids, d._id]
                      }))}
                    >
                      {d.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {sequences.length > 0 && (
            <div>
              <Label className="text-xs text-gray-500">Execution Sequences to include</Label>
              <p className="text-xs text-gray-400 mt-0.5">Scheduled runs and IMAP triggers will execute these sequences in addition to API defs.</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {sequences.map(s => {
                  const sel = form.sequence_ids.includes(s._id);
                  return (
                    <button
                      key={s._id}
                      className={`text-xs px-2 py-1 rounded border transition-colors flex items-center gap-1 ${sel ? "bg-[#006080] text-white border-[#006080]" : "bg-white border-gray-200 text-gray-600"}`}
                      onClick={() => setForm(f => ({
                        ...f,
                        sequence_ids: sel ? f.sequence_ids.filter(x => x !== s._id) : [...f.sequence_ids, s._id]
                      }))}
                    >
                      <span className="text-[9px]">⛓</span> {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TemplateLibraryPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [templates, setTemplates] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [apiDefs, setApiDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = React.useRef();

  useEffect(() => {
    loadAll();
    client.get("/files/").then(r => setUploads(r.data));
    client.get("/api-defs/").then(r => setApiDefs(r.data));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await client.get("/templates/");
      setTemplates(res.data);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (form) => {
    await client.post("/templates/save-from-upload", form);
    await loadAll();
  };

  const updateTemplate = async (form) => {
    await client.put(`/templates/${editingTemplate._id}`, form);
    await loadAll();
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await client.delete(`/templates/${id}`);
    loadAll();
  };

  const exportTemplate = (id) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/templates/${id}/export`, "_blank");
  };

  const importTemplate = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      await client.post("/templates/import", json);
      await loadAll();
    } catch (err) {
      alert("Failed to import template: " + err.message);
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Template Library</h1>
          <p className="text-gray-500 text-sm mt-0.5">Reusable configurations for your data pipelines</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={importTemplate} />
          <Button variant="outline" className="border-gray-200" onClick={() => fileInputRef.current?.click()} disabled={importing}>
            <Upload size={15} className="mr-2" /> {importing ? "Importing..." : "Import JSON"}
          </Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => setShowSaveModal(true)} data-testid="save-template-btn">
            <Plus size={15} className="mr-2" /> Save Template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-white border border-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <BookTemplate size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No templates yet</p>
          <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => setShowSaveModal(true)}>
            Save your first template
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <div key={t._id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-all" data-testid={`template-${t._id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-[#e6f2f5] rounded-md flex items-center justify-center flex-shrink-0">
                  <BookTemplate size={18} className="text-[#006080]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    {t.is_global && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#006080] text-white font-semibold flex items-center gap-0.5">
                        <ShieldCheck size={9} className="inline" /> Global
                      </span>
                    )}
                    {t.version > 1 && <span className="text-[10px] text-gray-400">v{t.version}</span>}
                  </div>
                  {t.description && <p className="text-xs text-gray-400 mt-0.5">{t.description}</p>}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Badge variant="secondary" className="text-xs">{t.column_schema?.length || 0} columns</Badge>
                    <Badge variant="secondary" className="text-xs">{t.pipeline?.length || 0} transforms</Badge>
                    <Badge variant="secondary" className="text-xs">{t.api_definitions?.length || 0} API defs</Badge>
                    {t.last_used_at && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Calendar size={11} /> Last used {new Date(t.last_used_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[#006080] text-[#006080] hover:bg-[#e6f2f5]" onClick={() => navigate(`/upload`)}>
                    <Play size={12} className="mr-1" /> Use
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditingTemplate(t)} data-testid={`edit-template-${t._id}`}>
                    <Edit2 size={12} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => exportTemplate(t._id)}>
                    <Download size={12} />
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50 hover:border-red-200" onClick={() => deleteTemplate(t._id)}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSaveModal && (
        <SaveTemplateModal
          uploads={uploads}
          apiDefs={apiDefs}
          isSuperAdmin={isSuperAdmin}
          onSave={saveTemplate}
          onClose={() => setShowSaveModal(false)}
        />
      )}
      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          isSuperAdmin={isSuperAdmin}
          onSave={updateTemplate}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </div>
  );
}
